'use client'

import { Dictionary } from '@/src/common/dictionaries/dictionary.type'
import { createContext, FC, ReactNode, useContext, useEffect, useMemo } from 'react'

interface VariableContextInterface {
  frontEndUrl: string
  backendUrl: string
  imageUrl: string
  lang: string
  dictionary: Dictionary
}
const VariableContext = createContext({
  frontEndUrl: '',
  backendUrl: '',
  imageUrl: '',
  lang: '',
  dictionary: {} as Dictionary,
} as VariableContextInterface)

export const VariableContextComponent: FC<VariableContextInterface & { children: ReactNode }> = (props) => {
  const { children, frontEndUrl, backendUrl, imageUrl, lang, dictionary } = props

  // Keyed on the fields rather than on the rest object: `...otherProps` builds a
  // fresh object every render, so a memo depending on it never hits, and every
  // consumer of useVariables re-rendered whenever this component's parent did.
  const memoizedProps = useMemo(
    () => ({ frontEndUrl, backendUrl, imageUrl, lang, dictionary }),
    [frontEndUrl, backendUrl, imageUrl, lang, dictionary]
  )

  useEffect(() => {
    if (typeof window !== 'undefined') {
      // @ts-expect-error - window.vars is not typed
      window.vars = memoizedProps
    }
  }, [memoizedProps])

  return <VariableContext.Provider value={memoizedProps}>{children}</VariableContext.Provider>
}

export const useVariables = () => {
  return useContext(VariableContext)
}

export const loadVars = () => {
  // @ts-expect-error - window.vars is not typed
  return window.vars as VariableContextInterface
}
